import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getCountryFlag } from '../utils/countries';
import { getPowerCategory, POWER_CATEGORIES } from '../constants/powerGroups';
import { getRiderWeightDrop, getRiderWeightSeries } from '../utils/weightControl';
import { getRelegationSummary } from '../utils/relegation';
import { downloadFile, readBlobError } from '../utils/fileDownload';
import RiderWeightAlert from './RiderWeightAlert';
import RelegationAlert from './RelegationAlert';
import RelegationModal from './RelegationModal';
import RiderCompareModal from './RiderCompareModal';
import ResultsPodium from './ResultsPodium';
import PositionProgressModal from './PositionProgressModal';
import WeightProgressModal from './WeightProgressModal';
import StageReplayModal from './StageReplayModal';
import RaceAnalysisModal from './RaceAnalysisModal';
import ShareResultModal from './ShareResultModal';
import EventSearchSelect from './EventSearchSelect';
import LoadMoreSentinel, { LIST_CHUNK_SIZE } from './LoadMoreSentinel';
import './EventResultsPage.css';
import './EventResultsPageMobileFilters.css';
import { formatTimeFromDate, formatNumericDate } from '../utils/timeFormat';

// Компонент для отображения медали с местом
const MedalIcon = ({ place }) => {
  const colors = {
    1: { main: '#FFD700', gradient: '#FFA500', shadow: '#B8860B' },
    2: { main: '#C0C0C0', gradient: '#E8E8E8', shadow: '#808080' },
    3: { main: '#CD7F32', gradient: '#E8A87C', shadow: '#8B4513' }
  };

  const color = colors[place];

  return (
    <div style={{ display: 'inline-block', width: '32px', height: '32px' }}>
      <svg width="32" height="32" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id={`medalGradient${place}`} cx="0.3" cy="0.3" r="0.8">
            <stop offset="0%" stopColor={color.gradient} />
            <stop offset="70%" stopColor={color.main} />
            <stop offset="100%" stopColor={color.shadow} />
          </radialGradient>
          <filter id={`shadow${place}`}>
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.3"/>
          </filter>
        </defs>
        
        <circle 
          cx="22" 
          cy="22" 
          r="18" 
          fill={`url(#medalGradient${place})`}
          filter={`url(#shadow${place})`}
          stroke={color.shadow}
          strokeWidth="1"
        />
        
        <circle 
          cx="22" 
          cy="22" 
          r="15" 
          fill="none"
          stroke={color.shadow}
          strokeWidth="0.5"
          opacity="0.4"
        />
        
        <circle 
          cx="22" 
          cy="22" 
          r="12" 
          fill="rgba(255,255,255,0.2)"
        />
        
        <ellipse 
          cx="18" 
          cy="16" 
          rx="6" 
          ry="4" 
          fill="rgba(255,255,255,0.4)" 
        />
        
        <path 
          d="M22,14 L23.5,18.5 L28,18.5 L24.5,21.5 L26,26 L22,23 L18,26 L19.5,21.5 L16,18.5 L20.5,18.5 Z"
          fill={color.shadow}
          opacity="0.3"
        />
        
        <text 
          x="22" 
          y="27" 
          fontSize="16" 
          fontWeight="bold" 
          fill="#000"
          textAnchor="middle"
        >
          {place}
        </text>
      </svg>
    </div>
  );
};

const getEventStatus = (event) => {
  // У открытого турнира расписания проездов нет: он идет ровно столько, сколько
  // длится период его проведения
  if (event.event_type === 'open_tournament') {
    const now = new Date();

    if (event.ends_at && new Date(event.ends_at) < now) return 'completed';
    if (event.starts_at && new Date(event.starts_at) > now) return 'planned';

    return 'active';
  }

  if (!event.event_routes || event.event_routes.length === 0) {
    return 'no_routes';
  }

  const now = new Date();
  const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
  const eventRoutes = event.event_routes;

  const pastRoutes = eventRoutes.filter(er => new Date(er.start_date_time_utc) < oneHourLater);
  const futureRoutes = eventRoutes.filter(er => new Date(er.start_date_time_utc) > oneHourLater);

  // Длительное событие идет, пока организатор не снимет флаг, даже если все проезды позади.
  // Скрытое длительное активным не считаем: участникам его не видно
  if (pastRoutes.length === eventRoutes.length) {
    return event.long_event && !event.hidden ? 'active' : 'completed';
  }
  if (futureRoutes.length === eventRoutes.length) {
    return 'planned';
  }
  return 'active';
};

// Результаты есть только у начавшихся событий, поэтому запланированные в список не попадают.
// Сначала идут текущие, за ними прошедшие; внутри группы сохраняется порядок бэкенда
const RESULTS_EVENT_STATUSES = ['active', 'completed'];

const visibleResultsEvents = (events) => events
  .filter(event => RESULTS_EVENT_STATUSES.includes(getEventStatus(event)))
  .sort((a, b) => RESULTS_EVENT_STATUSES.indexOf(getEventStatus(a)) -
                  RESULTS_EVENT_STATUSES.indexOf(getEventStatus(b)));

// embedded — рендер внутри модалки события: заголовок и рамку рисует она сама
const EventResultsPage = ({ selectedEventId = null, embedded = false }) => {
  const { t, language } = useLanguage();
  // Текущий пользователь — второй участник сравнения по умолчанию
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfError, setPdfError] = useState('');
  
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('individual');
  const [expandedRoutes, setExpandedRoutes] = useState({});
  const [expandedTeams, setExpandedTeams] = useState({});
  // Этап, на который смотрим личный зачет (null - актуальное состояние по всем этапам)
  const [selectedStageIndex, setSelectedStageIndex] = useState(null);
  // Участник, для которого открыт график прогресса по этапам
  const [progressRider, setProgressRider] = useState(null);
  // Участник, для которого открыт график динамики веса
  const [weightRider, setWeightRider] = useState(null);
  // Маршрут, для которого открыт визуальный реплей этапа
  const [replayRoute, setReplayRoute] = useState(null);
  // Маршрут, для которого открыт разбор гонки по участкам
  const [analysisRoute, setAnalysisRoute] = useState(null);
  // Этап и участник, для которых открыто сравнение показателей
  const [compareTarget, setCompareTarget] = useState(null);
  // Результат, для которого открыта карточка «поделиться»
  const [shareTarget, setShareTarget] = useState(null);
  // Гонщик, которому админ или организатор выписывает релегацию
  const [relegationRider, setRelegationRider] = useState(null);
  // Сколько строк показано в каждом списке протокола: { individual, team, route-<id> }.
  // Список рисуется порциями по LIST_CHUNK_SIZE, следующая — при докрутке до конца
  const [visibleCounts, setVisibleCounts] = useState({});


  
  // Фильтры
  const [filters, setFilters] = useState({
    ageGroup: '',
    powerGroup: '',
    team: '',
    country: '',
    gender: '',
    category: ''
  });
  
  // Мобильные фильтры
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState({
    country: '',
    powerCategory: '',
    ageCategory: '',
    gender: '',
    team: '',
    category: ''
  });
  
  const AGE_GROUPS = ['<19', '19-34', '35-39', '40-44', '45-49', '50-54', '55-59', '60-64', '65-69', '70-74', '75-79', '80+'];
  const POWER_GROUPS = POWER_CATEGORIES.map(c => c.cat);

  // Проверка на мобильное устройство
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Событие берем из пропса сразу: список событий гостю не отдается, а протокол
  // по прямой ссылке открывается и без него
  useEffect(() => {
    if (selectedEventId) {
      setSelectedEvent(selectedEventId);
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (selectedEvent) {
      fetchResults(selectedEvent);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/events');
      const eventsData = response.data || [];
      
      setEvents(eventsData);

      // Открываем первое из показанных событий, а не первое пришедшее:
      // запланированные отфильтрованы, и выбирать их нечего
      const visible = visibleResultsEvents(eventsData);
      if (!selectedEventId && visible.length > 0) {
        setSelectedEvent(visible[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (eventId) => {
    try {
      setLoading(true);
      // Не отправляем фильтры на бэкенд, применяем их на фронтенде
      const response = await axios.get(`/api/events/${eventId}/results`);
      setResults(response.data);
    } catch (err) {
      console.error('Failed to fetch results:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedEventData = events.find(e => String(e.id) === String(selectedEvent));

  const visibleEvents = visibleResultsEvents(events);
  // С плашки результаты открывают и у запланированного события — в отфильтрованный
  // список оно не попадает, но поле поиска не должно из-за этого выглядеть пустым
  if (selectedEventData && !visibleEvents.includes(selectedEventData)) {
    visibleEvents.unshift(selectedEventData);
  }
  const isSelectedEventCompleted = selectedEventData ? getEventStatus(selectedEventData) === 'completed' : false;

  useEffect(() => {
    if (activeTab === 'summary' && !isSelectedEventCompleted) {
      setActiveTab('individual');
    }
  }, [selectedEvent, isSelectedEventCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  // При смене события всегда показываем актуальное состояние зачета
  useEffect(() => {
    setSelectedStageIndex(null);
  }, [selectedEvent]);

  // Другая выборка — снова первая порция: иначе после фильтра или смены этапа
  // сразу рисовался бы весь накрученный раньше хвост
  useEffect(() => {
    setVisibleCounts({});
  }, [selectedEvent, activeTab, selectedStageIndex, filters, mobileFilters, isMobile]);

  const handleRecalculate = async () => {
    if (!selectedEvent || !window.confirm(t('results.confirm_recalculate'))) {
      return;
    }

    try {
      setRecalculating(true);
      await axios.post(`/api/events/${selectedEvent}/results/recalculate`);
      await fetchResults(selectedEvent);
      alert(t('results.recalculate_success'));
    } catch (err) {
      console.error('Failed to recalculate results:', err);
      alert(t('results.recalculate_error'));
    } finally {
      setRecalculating(false);
    }
  };



  const calculateAgeGroup = (birthDate) => {
    if (!birthDate) return null;
    
    const currentYear = new Date().getFullYear();
    const birthYear = new Date(birthDate).getFullYear();
    const age = currentYear - birthYear;
    
    if (age < 19) return '<19';
    if (age >= 19 && age <= 34) return '19-34';
    if (age >= 35 && age <= 39) return '35-39';
    if (age >= 40 && age <= 44) return '40-44';
    if (age >= 45 && age <= 49) return '45-49';
    if (age >= 50 && age <= 54) return '50-54';
    if (age >= 55 && age <= 59) return '55-59';
    if (age >= 60 && age <= 64) return '60-64';
    if (age >= 65 && age <= 69) return '65-69';
    if (age >= 70 && age <= 74) return '70-74';
    if (age >= 75 && age <= 79) return '75-79';
    if (age >= 80) return '80+';
    
    return null;
  };
  
  const calculatePowerGroup = getPowerCategory;

  const handlePowerBadgeClick = (powerGroup) => {
    setFilters(prev => ({
      ...prev,
      powerGroup: prev.powerGroup === powerGroup ? '' : powerGroup
    }));
  };

  const handleAgeBadgeClick = (ageGroup) => {
    setFilters(prev => ({
      ...prev,
      ageGroup: prev.ageGroup === ageGroup ? '' : ageGroup
    }));
  };

  const handleTeamBadgeClick = (teamName) => {
    if (!teamName) return;
    setFilters(prev => ({
      ...prev,
      team: prev.team === teamName ? '' : teamName
    }));
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'FINISHED':
        return t('results.status_finished');
      case 'DNS':
        return t('results.status_dns');
      case 'DNF':
        return t('results.status_dnf');
      default:
        return status;
    }
  };

  // Протокол печатает бэкенд: тем же расчетом, что отдает результаты на страницу,
  // и на языке интерфейса
  const handleDownloadPdf = async () => {
    if (!selectedEvent) return;

    setPdfExporting(true);
    setPdfError('');
    try {
      await downloadFile(`/api/events/${selectedEvent}/results/pdf`, {
        params: { language },
        fallbackName: `results-${selectedEvent}.pdf`
      });
    } catch (err) {
      console.error('Failed to download results PDF:', err);
      const details = await readBlobError(err);
      setPdfError([t('results.pdf_error'), details].filter(Boolean).join(': '));
    } finally {
      setPdfExporting(false);
    }
  };

  const formatTime = (preciseTime) => {
    if (!preciseTime) return '-';
    
    const totalSeconds = parseFloat(preciseTime);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    
    let result;
    if (hours > 0) {
      result = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      result = `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
    
    const fractional = totalSeconds - Math.floor(totalSeconds);
    const hundredths = Math.floor(fractional * 100);
    result += `.${String(hundredths).padStart(2, '0')}`;
    
    return result;
  };

  const formatTimeDifference = (diff) => {
    if (!diff || diff <= 0) return '';
    
    const totalSeconds = Math.abs(diff);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const fractional = totalSeconds - Math.floor(totalSeconds);
    const hundredths = Math.floor(fractional * 100);
    
    let result = '+';
    if (hours > 0) {
      result += `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      result += `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
    result += `.${String(hundredths).padStart(2, '0')}`;
    
    return result;
  };

  const formatRideDateTime = (dateTimeUtc) => {
    if (!dateTimeUtc) return null;
    
    const date = new Date(dateTimeUtc);
    const time = formatTimeFromDate(date, language);

    return t('results.ride_at')
      .replace('{date}', formatNumericDate(date, language))
      .replace('{time}', time);
  };

  const calculateTotalTime = (userId) => {
    if (!results || !results.route_details || results.route_details.length === 0) {
      return null;
    }
    
    let totalTime = 0;
    let finishedRoutesCount = 0;
    
    for (const routeDetail of results.route_details) {
      const userResult = routeDetail.results.find(r => r.user_id === userId);
      
      if (userResult && userResult.status === 'FINISHED' && userResult.finish_time) {
        totalTime += parseFloat(userResult.finish_time);
        finishedRoutesCount++;
      }
    }
    
    if (finishedRoutesCount > 0) {
      return totalTime;
    }

    return null;
  };

  // Результаты individual_results с посчитанными total_time и routes_completed,
  // без применения фильтров (используется для вкладки "Итоги")
  const getResultsWithComputedTime = () => {
    if (!results || !results.individual_results) return [];

    return results.individual_results.map(result => {
      const routesCompleted = results.route_details ?
        results.route_details.filter(rd =>
          rd.results.some(r => r.user_id === result.user_id && r.status === 'FINISHED')
        ).length : 0;

      return {
        ...result,
        total_time: calculateTotalTime(result.user_id),
        routes_completed: routesCompleted
      };
    });
  };

  const getVisibleCount = (listKey) => visibleCounts[listKey] || LIST_CHUNK_SIZE;

  // Метка подгрузки в конце списка; когда показано все — не нужна.
  // Math.max делает подгрузку идемпотентной: повторный сигнал той же метки не
  // перескакивает через порцию
  const renderLoadMore = (listKey, total) => {
    const shown = getVisibleCount(listKey);
    if (shown >= total) return null;

    return (
      <LoadMoreSentinel
        key={`${listKey}-${shown}`}
        onVisible={() => setVisibleCounts(prev => ({
          ...prev,
          [listKey]: Math.max(prev[listKey] || LIST_CHUNK_SIZE, shown + LIST_CHUNK_SIZE)
        }))}
      />
    );
  };

  const toggleRouteExpanded = (routeId) => {
    setExpandedRoutes(prev => ({
      ...prev,
      [routeId]: !prev[routeId]
    }));
  };

  const toggleTeamExpanded = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const formatShortName = (firstName, lastName, firstNameEng, lastNameEng) => {
    if (language !== 'ru' && firstNameEng && lastNameEng) {
      return `${firstNameEng.charAt(0)}. ${lastNameEng}`;
    }
    return `${firstName.charAt(0)}. ${lastName}`;
  };

  const filterResults = (resultsList) => {
    if (!resultsList) return [];
    
    const activeFilters = isMobile ? mobileFilters : filters;
    
    return resultsList.filter(result => {
      // Фильтр по возрастной группе
      if (activeFilters.ageGroup || activeFilters.ageCategory) {
        const userAgeGroup = calculateAgeGroup(result.birth_date);
        const filterValue = activeFilters.ageGroup || activeFilters.ageCategory;
        if (userAgeGroup !== filterValue) return false;
      }
      
      // Фильтр по силовой группе
      if (activeFilters.powerGroup || activeFilters.powerCategory) {
        const userPowerGroup = calculatePowerGroup(result.power_group);
        const filterValue = activeFilters.powerGroup || activeFilters.powerCategory;
        if (userPowerGroup !== filterValue) return false;
      }
      
      // Фильтр по команде
      if (activeFilters.team) {
        if (result.team_name !== activeFilters.team) return false;
      }
      
      // Фильтр по стране
      if (activeFilters.country) {
        if (result.country !== activeFilters.country) return false;
      }
      
      // Фильтр по полу
      if (activeFilters.gender) {
        if (result.gender !== activeFilters.gender) return false;
      }
      
      return true;
    });
  };

  // Расчет позиций общего зачета по произвольному набору этапов
  // (используется как для текущего зачета, так и для зачета на предыдущем этапе)
  const computeStandings = (resultsList, routeDetails) => {
    // Сначала рассчитываем количество завершенных этапов и общее время для каждого участника
    const resultsWithStats = resultsList.map(result => {
      let totalTime = 0;
      let routesCompleted = 0;
      let totalPoints = 0;
      let npSum = 0;
      let npCount = 0;

      (routeDetails || []).forEach(rd => {
        const userResult = rd.results.find(r => r.user_id === result.user_id);
        if (userResult && userResult.status === 'FINISHED' && userResult.finish_time) {
          totalTime += parseFloat(userResult.finish_time);
          routesCompleted++;
          totalPoints += userResult.points || 0;
          if (userResult.normalized_power) {
            npSum += parseFloat(userResult.normalized_power);
            npCount++;
          }
        }
      });

      return {
        ...result,
        routesCompleted,
        totalTime: routesCompleted > 0 ? totalTime : null,
        total_points: totalPoints,
        avg_normalized_power: npCount > 0 ? Math.round(npSum / npCount) : null
      };
    });

    // Сортируем: сначала по количеству завершенных этапов (больше лучше),
    // затем по времени (меньше лучше)
    const sorted = [...resultsWithStats].sort((a, b) => {
      // Сначала сравниваем количество завершенных этапов
      if (b.routesCompleted !== a.routesCompleted) {
        return b.routesCompleted - a.routesCompleted;
      }
      
      // Если количество этапов одинаковое, сравниваем по времени
      if (a.totalTime === null && b.totalTime === null) return 0;
      if (a.totalTime === null) return 1; // участники без времени идут после
      if (b.totalTime === null) return -1;
      
      return a.totalTime - b.totalTime; // меньше время = лучше позиция
    });
    
    return sorted.map((result, index) => ({
      ...result,
      position: index + 1
    }));
  };

  // Состоявшиеся этапы (с номером этапа турнира и маршрутом)
  const getCompletedStages = () => {
    const routeDetails = (results && results.route_details) || [];

    return routeDetails.reduce((stages, rd, index) => {
      if (rd.results.some(r => r.status === 'FINISHED' && r.finish_time)) {
        stages.push({ index, number: index + 1, route: rd.route });
      }
      return stages;
    }, []);
  };

  // Этапы, состоявшиеся до последнего проведенного этапа переданного набора.
  // Возвращает null, если предыдущего этапа нет (проведен только один этап или ни одного)
  const getRouteDetailsBeforeLastStage = (routeDetails) => {
    const stages = routeDetails || [];

    let lastStageIndex = -1;
    stages.forEach((rd, index) => {
      if (rd.results.some(r => r.status === 'FINISHED' && r.finish_time)) {
        lastStageIndex = index;
      }
    });

    if (lastStageIndex <= 0) return null;

    return stages.slice(0, lastStageIndex);
  };

  // Место участника в зачете после каждого этапа - с 1-го по текущий
  const buildRiderProgress = (userId, stageRouteDetails, standingsPool) => {
    return stageRouteDetails.map((rd, index) => {
      const standings = computeStandings(standingsPool, stageRouteDetails.slice(0, index + 1));
      const entry = standings.find(s => s.user_id === userId);
      const stageResult = rd.results.find(r => r.user_id === userId);

      return {
        stage: index + 1,
        routeName: rd.route ? rd.route.name : null,
        position: entry ? entry.position : null,
        finished: !!(stageResult && stageResult.status === 'FINISHED' && stageResult.finish_time)
      };
    });
  };

  const getRiderName = (result) => (
    language !== 'ru' && result.first_name_eng && result.last_name_eng
      ? `${result.first_name_eng} ${result.last_name_eng}`
      : `${result.first_name} ${result.last_name}`
  );

  // ФИО ведет на карточку гонщика: карьера, личные рекорды, кривая мощности
  const renderProfileName = (result) => (
    <Link className="rider-name-link" to={`/riders/${result.user_id}`} title={t('results.open_rider')}>
      {getRiderName(result)}
    </Link>
  );

  // Гостю недоступны реплей, разбор гонки и сравнение показателей: это тяжелые
  // расчеты по всей телеметрии этапа, они остались за авторизацией
  const isGuest = !currentUser;

  // Кнопка «поделиться» есть только у финишировавших — карточка рисуется по времени
  const renderShareButton = (result, routeDetail) => {
    if (result.status !== 'FINISHED') return null;

    return (
      <button
        type="button"
        className="results-page-share-button"
        title={t('share.open')}
        aria-label={t('share.open')}
        onClick={(e) => {
          e.stopPropagation();
          setShareTarget({
            routeId: routeDetail.route.id,
            userId: result.user_id,
            name: getRiderName(result)
          });
        }}
      >
        ↗
      </button>
    );
  };

  // Релегации выписывает админ или организатор события — флаг приходит с результатами
  const canManageRelegations = !!(results && results.event && results.event.can_manage_relegations);

  // Штраф имеет смысл только там, где есть время: предлагаем этапы, которые гонщик проехал
  const getRelegationStages = (userId) => {
    const routeDetails = (results && results.route_details) || [];

    return routeDetails.reduce((stages, rd, index) => {
      const userResult = rd.results.find(r => r.user_id === userId);
      if (userResult && userResult.status === 'FINISHED' && userResult.finish_time) {
        stages.push({ routeId: rd.route.id, number: index + 1, name: rd.route.name });
      }
      return stages;
    }, []);
  };

  const openRelegation = (result) => {
    setRelegationRider({
      user_id: result.user_id,
      name: getRiderName(result),
      stages: getRelegationStages(result.user_id),
      relegations: ((results && results.relegations) || []).filter(item => item.user_id === result.user_id)
    });
  };

  // Значок выдачи релегации рядом с ФИО: виден только тем, кто может ее выписать
  const renderRelegationButton = (result) => {
    if (!canManageRelegations) return null;

    return (
      <button
        type="button"
        className="relegation-edit-button"
        title={t('results.relegation_add')}
        aria-label={t('results.relegation_add')}
        onClick={(e) => {
          e.stopPropagation();
          openRelegation(result);
        }}
      >
        +
      </button>
    );
  };

  const openRiderProgress = (result, stageRouteDetails, standingsPool) => {
    setProgressRider({
      riderName: getRiderName(result),
      points: buildRiderProgress(result.user_id, stageRouteDetails, standingsPool)
    });
  };

  // ФИО в разделе "По маршрутам" открывает сравнение показателей.
  // У не проехавших этап телеметрии нет, поэтому имя остается обычным текстом
  const renderCompareName = (result, routeDetail) => {
    const name = getRiderName(result);

    if (result.status !== 'FINISHED') return name;
    if (isGuest) return renderProfileName(result);

    return (
      <button
        type="button"
        className="rider-name-link"
        title={t('results.compare_open')}
        onClick={() => openCompare(routeDetail, result.user_id)}
      >
        {name}
      </button>
    );
  };

  // Сравнение показателей на этапе: второй участник по умолчанию — текущий пользователь
  const openCompare = (routeDetail, userId) => {
    setCompareTarget({
      routeId: routeDetail.route.id,
      routeName: routeDetail.route.name,
      riderId: userId
    });
  };

  // График динамики веса: те же замеры, по которым ставится значок контроля веса
  const openRiderWeight = (result, stageRouteDetails) => {
    setWeightRider({
      riderName: getRiderName(result),
      points: getRiderWeightSeries(result.user_id, results.rider_weights, stageRouteDetails)
    });
  };

  // Изменение позиции относительно предыдущего этапа.
  // reserveSpace оставляет пустой блок фиксированной ширины, чтобы номера мест не смещались,
  // onOpenProgress открывает график прогресса участника по этапам
  const renderPositionChange = (change, reserveSpace, onOpenProgress) => {
    if (change === null || change === undefined) {
      return reserveSpace ? <span className="results-page-position-change" /> : null;
    }

    const stateClass = change === 0
      ? 'results-page-position-change-same'
      : (change > 0 ? 'results-page-position-change-up' : 'results-page-position-change-down');

    const stateTitle = change === 0
      ? t('results.position_change_same')
      : (change > 0 ? t('results.position_change_up') : t('results.position_change_down'));

    const improved = change > 0;

    return (
      <button
        type="button"
        className={`results-page-position-change ${stateClass}`}
        title={`${stateTitle} — ${t('results.progress_chart_open')}`}
        onClick={onOpenProgress}
      >
        {change === 0 ? '=' : (
          <>
            <svg width="11" height="11" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
              {improved ? <polygon points="5,1 9,9 1,9" /> : <polygon points="1,1 9,1 5,9" />}
            </svg>
            {Math.abs(change)}
          </>
        )}
      </button>
    );
  };

  const recalculateRoutePositions = (resultsList) => {
    // Сортируем по времени финиша (финишировавшие первыми, потом DNS/DNF)
    const finished = resultsList.filter(r => r.status === 'FINISHED' && r.finish_time);
    const notFinished = resultsList.filter(r => r.status !== 'FINISHED' || !r.finish_time);
    
    // Сортируем финишировавших по времени
    const sortedFinished = finished.sort((a, b) => parseFloat(a.finish_time) - parseFloat(b.finish_time));
    
    // Присваиваем позиции финишировавшим
    const withPositions = sortedFinished.map((result, index) => ({
      ...result,
      position: index + 1
    }));
    
    // Добавляем не финишировавших без позиций
    const notFinishedWithoutPos = notFinished.map(result => ({
      ...result,
      position: null
    }));
    
    return [...withPositions, ...notFinishedWithoutPos];
  };

  // Выбор этапа, после которого показывать состояние протокола
  const renderStageSelector = (completedStages) => {
    if (!completedStages || completedStages.length < 2) return null;

    const lastStage = completedStages[completedStages.length - 1];

    return (
      <div className="results-page-stage-selector">
        <label htmlFor="stage-snapshot-select">{t('results.stage_snapshot')}:</label>
        <select
          id="stage-snapshot-select"
          value={selectedStageIndex === null ? '' : String(selectedStageIndex)}
          onChange={(e) => setSelectedStageIndex(e.target.value === '' ? null : Number(e.target.value))}
        >
          <option value="">{t('results.stage_snapshot_current')}</option>
          {completedStages.map(stage => (
            <option key={stage.index} value={stage.index}>
              {t('results.stage_snapshot_after').replace('{number}', stage.number)}
              {/* Название маршрута только на десктопе, чтобы селект не разрастался */}
              {!isMobile && stage.route && stage.route.name ? ` — ${stage.route.name}` : ''}
            </option>
          ))}
        </select>
        {selectedStageIndex !== null && selectedStageIndex !== lastStage.index && (
          <span className="results-page-stage-selector-hint">
            {t('results.stage_snapshot_hint')}
          </span>
        )}
      </div>
    );
  };

  const renderIndividualResults = () => {
    if (!results || !results.individual_results || results.individual_results.length === 0) {
      return <div className="results-page-no-results">{t('results.no_individual_results')}</div>;
    }
    
    const allRouteDetails = results.route_details || [];
    const completedStages = getCompletedStages();

    // Срез этапов, по которому строим протокол:
    // выбранный этап включительно или все этапы (актуальное состояние)
    const stageRouteDetails = selectedStageIndex !== null
      ? allRouteDetails.slice(0, selectedStageIndex + 1)
      : allRouteDetails;

    const filteredResults = filterResults(results.individual_results);
    const recalculatedResults = computeStandings(filteredResults, stageRouteDetails);
    const displayResults = recalculatedResults.slice(0, getVisibleCount('individual'));

    if (displayResults.length === 0) {
      return (
        <>
          {renderStageSelector(completedStages)}
          <div className="results-page-no-results">{t('results.no_results_with_filters')}</div>
        </>
      );
    }

    // Позиции в зачете на предыдущем этапе (для отображения динамики)
    const previousRouteDetails = getRouteDetailsBeforeLastStage(stageRouteDetails);
    const previousPositions = previousRouteDetails
      ? computeStandings(filteredResults, previousRouteDetails).reduce((acc, result) => {
          acc[result.user_id] = result.position;
          return acc;
        }, {})
      : null;

    const maxRoutesCompleted = recalculatedResults.reduce(
      (max, result) => Math.max(max, result.routesCompleted),
      0
    );

    const bestTime = recalculatedResults.reduce((best, result) => {
      if (result.routesCompleted === maxRoutesCompleted &&
          result.totalTime !== null &&
          (best === null || result.totalTime < best)) {
        return result.totalTime;
      }
      return best;
    }, null);

    return (
      <div className="results-page-table-wrapper">
        {renderStageSelector(completedStages)}
        <table className="results-page-table">
          {!isMobile && (
            <thead>
              <tr>
                <th style={{ width: '60px' }}>{t('results.position')}</th>
                <th style={{ width: '200px' }}>{t('results.rider')}</th>
                <th style={{ width: '120px' }}>{t('results.time')}</th>
                <th style={{ width: '80px' }}>{t('results.points')}</th>
                <th style={{ width: '80px' }}>NP</th>
                <th style={{ width: '120px' }}>{t('results.team')}</th>
                <th style={{ width: '80px' }}>{t('results.power')}</th>
                <th style={{ width: '80px' }}>{t('profile.age')}</th>
              </tr>
            </thead>
          )}
          <tbody>
            {displayResults.map((result) => {
              const routesCompleted = result.routesCompleted;
              const totalRoutes = stageRouteDetails.length;
              const totalTime = result.totalTime;
              const displayPosition = result.position;
              const medalColors = {
                1: 'rgba(255, 215, 0, 0.15)',
                2: 'rgba(192, 192, 192, 0.15)',
                3: 'rgba(205, 127, 50, 0.15)'
              };
              const backgroundColor = displayPosition <= 3 && result.total_points > 0 ? medalColors[displayPosition] : 'transparent';
              const positionChange = previousPositions && previousPositions[result.user_id]
                ? previousPositions[result.user_id] - displayPosition
                : null;
              // Контроль веса по соревновательным заездам участника не позже выбранного этапа
              const weightDrop = getRiderWeightDrop(result.user_id, results.rider_weights, stageRouteDetails);
              // Штрафы релегаций тех же этапов: их время уже входит в totalTime
              const relegationSummary = getRelegationSummary(result.user_id, stageRouteDetails);

              return (
                <tr key={result.user_id} style={{ backgroundColor }}>
                  <td className="results-page-position-cell">
                    {/* Место и динамика занимают слоты фиксированной ширины:
                        медали и номера стоят на одной вертикали, динамика не сдвигает место.
                        Зеркальная распорка слева удерживает номер по центру колонки */}
                    <div className="results-page-position-wrapper">
                      {previousPositions !== null && (
                        <span className="results-page-position-change-spacer" aria-hidden="true" />
                      )}
                      <span className="results-page-position-value">
                        {displayPosition <= 3 && result.total_points > 0 ? (
                          <MedalIcon place={displayPosition} />
                        ) : (
                          displayPosition
                        )}
                      </span>
                      {renderPositionChange(
                        positionChange,
                        previousPositions !== null,
                        () => openRiderProgress(result, stageRouteDetails, filteredResults)
                      )}
                    </div>
                  </td>
                  <td>
                    {isMobile ? (
                      <div className="results-page-rider-info">
                        <div className="results-page-rider-icons">
                          {result.gender && (
                            <span className={`results-page-gender-icon ${result.gender === 'male' ? 'results-page-gender-male' : 'results-page-gender-female'}`}>
                              {result.gender === 'male' ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="10" cy="14" r="5"/>
                                  <path d="M19 5l-7 7M19 5h-5M19 5v5"/>
                                </svg>
                              ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="8" r="5"/>
                                  <path d="M12 13v8M9 18h6"/>
                                </svg>
                              )}
                            </span>
                          )}
                          {result.country && getCountryFlag(result.country) && (
                            <span className="results-page-country-flag-icon" style={{ fontSize: '20px', lineHeight: '1' }}>
                              {getCountryFlag(result.country)}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="results-page-rider-name">
                            {renderProfileName(result)}
                            {/* На мобильных подсказки по наведению нет — значок открывает график */}
                            <RiderWeightAlert
                              weightDrop={weightDrop}
                              onOpenChart={() => openRiderWeight(result, stageRouteDetails)}
                            />
                            {renderRelegationButton(result)}
                          </div>
                          <div className="results-page-rider-nickname">@{result.nickname}</div>
                          <div className="results-page-routes-info" style={{ fontSize: '0.85em', color: 'var(--c-6c757d-fg)', marginTop: '4px' }}>
                            {t('results.routes_completed')}: {routesCompleted}/{totalRoutes}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {result.gender && (
                          <span className={`results-page-gender-icon ${result.gender === 'male' ? 'results-page-gender-male' : 'results-page-gender-female'}`}>
                            {result.gender === 'male' ? (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="10" cy="14" r="5"/>
                                <path d="M19 5l-7 7M19 5h-5M19 5v5"/>
                              </svg>
                            ) : (
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="8" r="5"/>
                                <path d="M12 13v8M9 18h6"/>
                              </svg>
                            )}
                          </span>
                        )}
                        {result.country && getCountryFlag(result.country) && (
                          <span className="results-page-country-flag-icon" style={{ fontSize: '20px', lineHeight: '1' }}>
                            {getCountryFlag(result.country)}
                          </span>
                        )}
                        <div>
                          <div className="results-page-rider-name">
                            {renderProfileName(result)}
                            <RiderWeightAlert weightDrop={weightDrop} />
                            {renderRelegationButton(result)}
                          </div>
                          <div className="results-page-rider-nickname">@{result.nickname}</div>
                          <div className="results-page-routes-info" style={{ fontSize: '0.85em', color: 'var(--c-6c757d-fg)', marginTop: '4px' }}>
                            {t('results.routes_completed')}: {routesCompleted}/{totalRoutes}
                          </div>
                        </div>
                      </div>
                    )}
                  </td>
                  {!isMobile ? (
                    <>
                      <td style={{ textAlign: 'center' }}>
                        {totalTime !== null ? (
                          <div>
                            <div>
                              <span className={relegationSummary ? 'relegation-time-anchor' : undefined}>
                                <strong>{formatTime(totalTime)}</strong>
                                {relegationSummary && <RelegationAlert entries={relegationSummary.entries} />}
                              </span>
                            </div>
                            {displayPosition > 1 && 
                             bestTime !== null && 
                             routesCompleted === maxRoutesCompleted && (
                              <div style={{ fontSize: '0.85em', color: 'var(--c-880202-fg)' }}><strong>{formatTimeDifference(totalTime - bestTime)}</strong></div>
                            )}
                          </div>
                        ) : (
                          '---'
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}><strong>{result.total_points}</strong></td>
                      <td style={{ textAlign: 'center' }}><strong>{result.avg_normalized_power || '-'}</strong></td>
                      <td 
                        style={{ cursor: result.team_name ? 'pointer' : 'default', color: filters.team === result.team_name ? 'var(--c-007bff-fg)' : 'inherit', fontWeight: filters.team === result.team_name ? 'bold' : 'normal' }}
                        onClick={() => handleTeamBadgeClick(result.team_name)}
                        title={result.team_name ? t('results.click_to_filter_team') : ''}
                      >
                        {result.team_name || '-'}
                      </td>
                      <td 
                        style={{ textAlign: 'center', cursor: 'pointer', color: filters.powerGroup === calculatePowerGroup(result.power_group) ? 'var(--c-007bff-fg)' : 'inherit', fontWeight: filters.powerGroup === calculatePowerGroup(result.power_group) ? 'bold' : 'normal' }}
                        onClick={() => handlePowerBadgeClick(calculatePowerGroup(result.power_group))}
                        title={t('results.click_to_filter_power')}
                      >
                        {calculatePowerGroup(result.power_group) || '-'}
                      </td>
                      <td 
                        style={{ textAlign: 'center', cursor: 'pointer', color: filters.ageGroup === calculateAgeGroup(result.birth_date) ? 'var(--c-007bff-fg)' : 'inherit', fontWeight: filters.ageGroup === calculateAgeGroup(result.birth_date) ? 'bold' : 'normal' }}
                        onClick={() => handleAgeBadgeClick(calculateAgeGroup(result.birth_date))}
                        title={t('results.click_to_filter_age')}
                      >
                        {calculateAgeGroup(result.birth_date)}
                      </td>
                    </>
                  ) : (
                    <td>
                      <div className="results-page-data-cell">
                        <div className="results-page-time-badge results-page-data-time-text" style={{ marginTop: '4px', display: 'block' }}>
                          {totalTime !== null ? (
                            <>
                              <strong>{formatTime(totalTime)}</strong>
                              {relegationSummary && <RelegationAlert entries={relegationSummary.entries} />}
                              {displayPosition > 1 && 
                                bestTime !== null && 
                                routesCompleted === maxRoutesCompleted && (
                                <span className="results-page-badge-diff-text" style={{ marginLeft: '8px' }}><strong>{formatTimeDifference(totalTime - bestTime)}</strong></span>
                              )}
                            </>
                          ) : (
                            '---'
                          )}
                        </div>
                        {result.team_name && (
                          <div className="results-page-mobile-team-name">{result.team_name}</div>
                        )}
                        <div className="results-page-mobile-data-grid">
                          <div className="results-page-mobile-data-item">{t('profile.age')}: <strong>{calculateAgeGroup(result.birth_date)}</strong></div>
                          <div className="results-page-mobile-data-item">{t('results.points')}: <strong>{result.total_points}</strong></div>
                          <div className="results-page-mobile-data-item">{t('results.power')}: <strong>{calculatePowerGroup(result.power_group) || '-'}</strong></div>
                          <div className="results-page-mobile-data-item">NP: <strong>{result.avg_normalized_power || '-'}</strong></div>
                        </div>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {renderLoadMore('individual', recalculatedResults.length)}
      </div>
    );
  };

  const renderTeamResults = () => {
    if (!results || !results.team_results || results.team_results.length === 0) {
      return <div className="results-page-no-results">{t('results.no_team_results')}</div>;
    }

    const displayResults = results.team_results.slice(0, getVisibleCount('team'));

    return (
      <div className="results-page-table-wrapper">
        <table className="results-page-table">
          <thead>
            <tr>
              <th>{t('results.position')}</th>
              <th>{t('results.team')}</th>
              <th>{t('results.members')}</th>
              <th>{t('results.points')}</th>
              <th style={{ width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {displayResults.map((result) => {
              const isExpanded = expandedTeams[result.team_id];
              const hasParticipants = result.counted_members && result.counted_members.length > 0;
              const medalColors = {
                1: 'rgba(255, 215, 0, 0.15)',
                2: 'rgba(192, 192, 192, 0.15)',
                3: 'rgba(205, 127, 50, 0.15)'
              };
              const backgroundColor = result.position <= 3 && result.members_count > 0 ? medalColors[result.position] : 'transparent';
              
              return (
                <React.Fragment key={result.team_id}>
                  <tr className={hasParticipants ? 'results-page-team-row-expandable' : ''} style={{ backgroundColor }}>
                    <td className="results-page-position-cell">
                      {result.position <= 3 && result.members_count > 0 ? (
                        <MedalIcon place={result.position} />
                      ) : (
                        result.position
                      )}
                    </td>
                    <td>{result.team_name}</td>
                    <td className="results-page-members-cell">{result.members_count}</td>
                    <td className="results-page-points-cell">{result.total_points}</td>
                    <td className="results-page-expand-cell">
                      {hasParticipants && (
                        <button 
                          className="results-page-expand-button"
                          onClick={() => toggleTeamExpanded(result.team_id)}
                          title={isExpanded ? t('results.hide_participants') : t('results.show_participants')}
                        >
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{ 
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', 
                              transition: 'transform 0.3s' 
                            }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && hasParticipants && (
                    <tr className="results-page-team-participants-row">
                      <td colSpan="5">
                        <div className="results-page-participants-container">
                          <div className="results-page-participants-title">{t('results.counted_participants')}:</div>
                          <div className="results-page-participants-list">
                            {result.counted_members.map((member, idx) => (
                              <div key={idx} className="results-page-participant-item">
                                <span className="results-page-participant-name">
                                  {formatShortName(
                                    member.first_name, 
                                    member.last_name,
                                    member.first_name_eng,
                                    member.last_name_eng
                                  )}
                                </span>
                                <span className="results-page-participant-points">{member.points}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {renderLoadMore('team', results.team_results.length)}
      </div>
    );
  };

  const renderRouteResults = () => {
    if (!results || !results.route_details || results.route_details.length === 0) {
      return <div className="results-page-no-results">{t('results.no_routes')}</div>;
    }

    return (
      <div className="results-page-routes-results">
        {results.route_details.map((routeDetail, routeIndex) => {
          const isExpanded = expandedRoutes[routeDetail.route.id];
          const filteredRouteResults = filterResults(routeDetail.results);
          const recalculatedRouteResults = recalculateRoutePositions(filteredRouteResults);
          const routeListKey = `route-${routeDetail.route.id}`;
          const displayResults = recalculatedRouteResults.slice(0, getVisibleCount(routeListKey));
          
          if (displayResults.length === 0) return null;
          
          const bestTime = recalculatedRouteResults.reduce((best, result) => {
            if (result.status === 'FINISHED' && result.finish_time && (best === null || parseFloat(result.finish_time) < best)) {
              return parseFloat(result.finish_time);
            }
            return best;
          }, null);

          return (
            <div key={routeDetail.route.id} className="results-page-route-results-section">
              <div 
                className="results-page-route-header"
                onClick={() => toggleRouteExpanded(routeDetail.route.id)}
              >
                <h3 className="results-page-route-name">{routeIndex + 1}. {routeDetail.route.name}</h3>
                {/* На узких экранах кнопки встают в столбик — см. CSS */}
                <div className="results-page-route-actions">
                  {!isGuest && (
                  <button
                    className="results-page-replay-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setReplayRoute({ id: routeDetail.route.id, name: routeDetail.route.name });
                    }}
                    title={t('results.replay_open')}
                  >
                    ▶ {t('results.replay_button')}
                  </button>
                  )}
                  {!isGuest && (
                  <button
                    className="results-page-analysis-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnalysisRoute({ id: routeDetail.route.id, name: routeDetail.route.name });
                    }}
                    title={t('results.analysis_open')}
                  >
                    {t('results.analysis_button')}
                  </button>
                  )}
                </div>
                <button className="results-page-toggle-icon">
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>
              
              {isExpanded && (
                <>
                  <div className="results-page-table-wrapper">
                    <table className="results-page-table results-page-route-results-table">
                      {!isMobile && (
                        <thead>
                          <tr>
                            <th style={{ width: '60px' }}>{t('results.position')}</th>
                            <th style={{ width: '200px' }}>{t('results.rider')}</th>
                            <th style={{ width: '120px' }}>{t('results.time')}</th>
                            <th style={{ width: '80px' }}>{t('results.points')}</th>
                            <th style={{ width: '80px' }}>NP</th>
                            <th style={{ width: '200px' }}>{t('results.team')}</th>
                            <th style={{ width: '80px' }}>{t('results.power')}</th>
                            <th style={{ width: '80px' }}>{t('profile.age')}</th>
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {displayResults.map((result, index) => {
                          const displayPosition = result.position;
                          const medalColors = {
                            1: 'rgba(255, 215, 0, 0.15)',
                            2: 'rgba(192, 192, 192, 0.15)',
                            3: 'rgba(205, 127, 50, 0.15)'
                          };
                          const backgroundColor = displayPosition <= 3 ? medalColors[displayPosition] : 'transparent';
                          // Контроль веса на момент этого этапа: заезды позже него не учитываем
                          const weightDrop = getRiderWeightDrop(result.user_id, results.rider_weights, [routeDetail]);
                          // Релегация показывается на том этапе, за который выписана;
                          // ее время уже входит в finish_time результата
                          const stageRelegation = result.status === 'FINISHED' && result.relegation
                            ? [{
                                stage: routeIndex + 1,
                                routeName: routeDetail.route.name,
                                seconds: result.relegation.seconds,
                                reason: result.relegation.reason
                              }]
                            : null;
                        
                          return (
                            <tr key={`${result.user_id}-${index}`} className={`results-page-status-${result.status.toLowerCase()}`} style={{ backgroundColor }}>
                              <td className="results-page-position-cell">
                                {displayPosition && displayPosition <= 3 ? (
                                  <MedalIcon place={displayPosition} />
                                ) : (
                                  displayPosition || '-'
                                )}
                              </td>
                              <td>
                                {isMobile ? (
                                  <div className="results-page-rider-info">
                                    <div className="results-page-rider-icons">
                                      {result.gender && (
                                        <span className={`results-page-gender-icon ${result.gender === 'male' ? 'results-page-gender-male' : 'results-page-gender-female'}`}>
                                          {result.gender === 'male' ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="10" cy="14" r="5"/>
                                              <path d="M19 5l-7 7M19 5h-5M19 5v5"/>
                                            </svg>
                                          ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="12" cy="8" r="5"/>
                                              <path d="M12 13v8M9 18h6"/>
                                            </svg>
                                          )}
                                        </span>
                                      )}
                                      {result.country && getCountryFlag(result.country) && (
                                        <span className="results-page-country-flag-icon" style={{ fontSize: '20px', lineHeight: '1' }}>
                                          {getCountryFlag(result.country)}
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      <div className="results-page-rider-name">
                                        {renderCompareName(result, routeDetail)}
                                        {/* На мобильных подсказки по наведению нет — значок открывает график */}
                                        <RiderWeightAlert
                                          weightDrop={weightDrop}
                                          onOpenChart={() => openRiderWeight(result, [routeDetail])}
                                        />
                                        {renderRelegationButton(result)}
                                        {renderShareButton(result, routeDetail)}
                                      </div>
                                      <div className="results-page-rider-nickname">@{result.nickname}</div>
                                      {result.activity_start_date && (
                                        <div className="results-page-ride-datetime-info" style={{ fontSize: '0.75em', color: 'var(--c-999999-fg)', marginTop: '2px' }}>
                                          {formatRideDateTime(result.activity_start_date)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {result.gender && (
                                      <span className={`results-page-gender-icon ${result.gender === 'male' ? 'results-page-gender-male' : 'results-page-gender-female'}`}>
                                        {result.gender === 'male' ? (
                                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="10" cy="14" r="5"/>
                                            <path d="M19 5l-7 7M19 5h-5M19 5v5"/>
                                          </svg>
                                        ) : (
                                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="8" r="5"/>
                                            <path d="M12 13v8M9 18h6"/>
                                          </svg>
                                        )}
                                      </span>
                                    )}
                                    {result.country && getCountryFlag(result.country) && (
                                      <span className="results-page-country-flag-icon" style={{ fontSize: '20px', lineHeight: '1' }}>
                                        {getCountryFlag(result.country)}
                                      </span>
                                    )}
                                    <div>
                                      <div className="results-page-rider-name">
                                        {renderCompareName(result, routeDetail)}
                                        <RiderWeightAlert weightDrop={weightDrop} />
                                        {renderRelegationButton(result)}
                                        {renderShareButton(result, routeDetail)}
                                      </div>
                                      <div className="results-page-rider-nickname">@{result.nickname}</div>
                                      {result.activity_start_date && (
                                        <div style={{ fontSize: '0.75em', color: 'var(--c-999999-fg)', marginTop: '2px' }}>
                                          {formatRideDateTime(result.activity_start_date)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </td>
                              {!isMobile ? (
                                <>
                                  <td style={{ textAlign: 'center' }}>
                                    {result.status === 'FINISHED' && result.finish_time ? (
                                      <div>
                                        <div>
                                          <span className={stageRelegation ? 'relegation-time-anchor' : undefined}>
                                            <strong>{formatTime(result.finish_time)}</strong>
                                            {stageRelegation && <RelegationAlert entries={stageRelegation} />}
                                          </span>
                                        </div>
                                        {displayPosition > 1 && bestTime !== null && (
                                          <div style={{ fontSize: '0.85em', color: 'var(--c-880202-fg)' }}><strong>{formatTimeDifference(parseFloat(result.finish_time) - bestTime)}</strong></div>
                                        )}
                                      </div>
                                    ) : (
                                      <strong>{getStatusLabel(result.status)}</strong>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'center' }}><strong>{result.points}</strong></td>
                                  <td style={{ textAlign: 'center' }}><strong>{result.normalized_power || '-'}</strong></td>
                                  <td 
                                    style={{ cursor: result.team_name ? 'pointer' : 'default', color: filters.team === result.team_name ? 'var(--c-007bff-fg)' : 'inherit', fontWeight: filters.team === result.team_name ? 'bold' : 'normal' }}
                                    onClick={() => handleTeamBadgeClick(result.team_name)}
                                    title={result.team_name ? t('results.click_to_filter_team') : ''}
                                  >
                                    {result.team_name || '-'}
                                  </td>
                                  <td 
                                    style={{ textAlign: 'center', cursor: 'pointer', color: filters.powerGroup === calculatePowerGroup(result.power_group) ? 'var(--c-007bff-fg)' : 'inherit', fontWeight: filters.powerGroup === calculatePowerGroup(result.power_group) ? 'bold' : 'normal' }}
                                    onClick={() => handlePowerBadgeClick(calculatePowerGroup(result.power_group))}
                                    title={t('results.click_to_filter_power')}
                                  >
                                    {calculatePowerGroup(result.power_group) || '-'}
                                  </td>
                                  <td 
                                    style={{ textAlign: 'center', cursor: 'pointer', color: filters.ageGroup === calculateAgeGroup(result.birth_date) ? 'var(--c-007bff-fg)' : 'inherit', fontWeight: filters.ageGroup === calculateAgeGroup(result.birth_date) ? 'bold' : 'normal' }}
                                    onClick={() => handleAgeBadgeClick(calculateAgeGroup(result.birth_date))}
                                    title={t('results.click_to_filter_age')}
                                  >
                                    {calculateAgeGroup(result.birth_date)}
                                  </td>
                                </>
                              ) : (
                                <td>
                                  <div className="results-page-data-cell">
                                    <div className="results-page-time-badge results-page-data-time-text" style={{ marginTop: '4px', display: 'block' }}>
                                      {result.status === 'FINISHED' && result.finish_time ? (
                                        <>
                                          <strong>{formatTime(result.finish_time)}</strong>
                                          {stageRelegation && <RelegationAlert entries={stageRelegation} />}
                                          {displayPosition > 1 && bestTime !== null && (
                                            <span className="results-page-badge-diff-text" style={{ marginLeft: '8px' }}><strong>{formatTimeDifference(parseFloat(result.finish_time) - bestTime)}</strong></span>
                                          )}
                                        </>
                                      ) : (
                                        getStatusLabel(result.status)
                                      )}
                                    </div>
                                    <div className="results-page-mobile-team-name">{result.team_name || t('common.notSpecified')}</div>
                                    <div className="results-page-mobile-data-grid">
                                      <div className="results-page-mobile-data-item">{t('profile.age')}: <strong>{calculateAgeGroup(result.birth_date)}</strong></div>
                                      <div className="results-page-mobile-data-item">{t('results.points')}: <strong>{result.points}</strong></div>
                                      <div className="results-page-mobile-data-item">{t('results.power')}: <strong>{calculatePowerGroup(result.power_group) || '-'}</strong></div>
                                      <div className="results-page-mobile-data-item">NP: <strong>{result.normalized_power || '-'}</strong></div>
                                    </div>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {renderLoadMore(routeListKey, recalculatedRouteResults.length)}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSummary = () => {
    const enrichedResults = getResultsWithComputedTime();

    if (enrichedResults.length === 0) {
      return <div className="no-results">{t('results.no_individual_results')}</div>;
    }

    return (
      <ResultsPodium
        results={enrichedResults}
        calculateAgeGroup={calculateAgeGroup}
        calculatePowerGroup={calculatePowerGroup}
        formatTime={formatTime}
        language={language}
        t={t}
      />
    );
  };

  const containerClass = [
    'results-page-container',
    isMobile ? 'results-page-mobile-view' : '',
    embedded ? 'results-page-embedded' : ''
  ].filter(Boolean).join(' ');

  if (loading && !results) {
    return (
      <div className={containerClass}>
        <div className="results-page-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <div className="results-page-header">
        {!embedded && <h2>{t('results.title')}</h2>}

        <div className="results-page-top-block">
          {/* Гость приходит по прямой ссылке: выбирать ему не из чего — список
              событий и пересчет остались за авторизацией, поэтому показываем
              просто название события */}
          {isGuest && results?.event?.name && (
            <div className="results-page-public-title">{results.event.name}</div>
          )}
          <div className="results-page-event-selector">
            {!isGuest && (
            <EventSearchSelect
              options={visibleEvents.map(event => ({
                id: event.id,
                name: event.name,
                status: getEventStatus(event)
              }))}
              value={selectedEvent || ''}
              onChange={setSelectedEvent}
              disabled={loading}
              placeholder={t('results.event_search_placeholder')}
              emptyText={t('results.event_search_empty')}
            />
            )}
            {!isGuest && (
            <button 
              className="results-page-recalculate-button"
              onClick={handleRecalculate}
              disabled={!selectedEvent || recalculating || loading}
            >
              {recalculating ? t('results.recalculating') : t('results.recalculate')}
            </button>
            )}

            {!isGuest && (
            <button
              className="results-page-pdf-button"
              onClick={handleDownloadPdf}
              disabled={!selectedEvent || pdfExporting || loading || !results}
              title={t('results.pdf_hint')}
            >
              {pdfExporting ? t('results.pdf_preparing') : t('results.pdf_download')}
            </button>
            )}

            {pdfError && <div className="results-page-pdf-error">{pdfError}</div>}

            {isMobile && selectedEvent && results && (
              <div className="results-page-users-stats-bar">
                {(mobileFilters.country || mobileFilters.powerCategory || mobileFilters.ageCategory || mobileFilters.gender || mobileFilters.team) && (
                  <button onClick={() => setMobileFilters({ country: '', powerCategory: '', ageCategory: '', gender: '', team: '', category: '' })} className="results-page-clear-filters-btn">
                    {t('users.clearFilters')}
                  </button>
                )}
                <div className="results-page-mobile-filters-wrapper">
                  <button 
                    className="results-page-mobile-filters-toggle"
                    onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                  >
                    <span>{t('users.filters')}</span>
                    <span className={`results-page-mobile-filters-arrow ${mobileFiltersOpen ? 'results-page-mobile-filters-arrow-open' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  <div className={`results-page-mobile-filters-panel ${mobileFiltersOpen ? 'results-page-mobile-filters-panel-open' : ''}`}>
                    <div className="results-page-mobile-filters-grid">
                      <div className="results-page-mobile-filters-item">
                        <label htmlFor="mobile-filter-country">{t('users.country')}:</label>
                        <select
                          id="mobile-filter-country"
                          value={mobileFilters.country}
                          onChange={(e) => setMobileFilters(prev => ({ ...prev, country: e.target.value }))}
                          className="results-page-mobile-filters-select"
                        >
                          <option value="">{t('users.allCountries')}</option>
                          {results && results.individual_results && Array.from(new Set(results.individual_results.map(r => r.country).filter(Boolean))).sort().map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="results-page-mobile-filters-item">
                        <label htmlFor="mobile-filter-team">{t('users.team')}:</label>
                        <select
                          id="mobile-filter-team"
                          value={mobileFilters.team}
                          onChange={(e) => setMobileFilters(prev => ({ ...prev, team: e.target.value }))}
                          className="results-page-mobile-filters-select"
                        >
                          <option value="">{t('users.allTeams')}</option>
                          {results && results.individual_results && Array.from(new Set(results.individual_results.map(r => r.team_name).filter(Boolean))).sort().map(team => (
                            <option key={team} value={team}>{team}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="results-page-mobile-filters-item">
                        <label htmlFor="mobile-filter-power" dangerouslySetInnerHTML={{ __html: t('users.powerGroup') }}></label>
                        <select
                          id="mobile-filter-power"
                          value={mobileFilters.powerCategory}
                          onChange={(e) => setMobileFilters(prev => ({ ...prev, powerCategory: e.target.value }))}
                          className="results-page-mobile-filters-select"
                        >
                          <option value="">{t('users.allGroups')}</option>
                          {POWER_GROUPS.map(group => (
                            <option key={group} value={group}>{t('users.group')} {group}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="results-page-mobile-filters-item">
                        <label htmlFor="mobile-filter-age" dangerouslySetInnerHTML={{ __html: t('users.ageGroup') }}></label>
                        <select
                          id="mobile-filter-age"
                          value={mobileFilters.ageCategory}
                          onChange={(e) => setMobileFilters(prev => ({ ...prev, ageCategory: e.target.value }))}
                          className="results-page-mobile-filters-select"
                        >
                          <option value="">{t('users.allAgeGroups')}</option>
                          {AGE_GROUPS.map(group => (
                            <option key={group} value={group}>{group}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="results-page-mobile-filters-item">
                        <label htmlFor="mobile-filter-gender">{t('profile.gender')}:</label>
                        <select
                          id="mobile-filter-gender"
                          value={mobileFilters.gender}
                          onChange={(e) => setMobileFilters(prev => ({ ...prev, gender: e.target.value }))}
                          className="results-page-mobile-filters-select"
                        >
                          <option value="">{t('profile.allGenders')}</option>
                          <option value="male">{t('profile.male')}</option>
                          <option value="female">{t('profile.female')}</option>
                        </select>
                      </div>
                    </div>
                    
                    <button 
                      className="results-page-mobile-filters-clear"
                      onClick={() => setMobileFilters({ country: '', powerCategory: '', ageCategory: '', gender: '', team: '', category: '' })}
                    >
                      {t('users.clearFilters')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="results-page-body">
        {loading ? (
          <div className="results-page-loading">{t('common.loading')}</div>
        ) : results ? (
          <>
            {((filters.ageGroup || filters.powerGroup || filters.team) || 
             (isMobile && (mobileFilters.gender || mobileFilters.powerCategory || mobileFilters.ageCategory || mobileFilters.country))) && (
              <div className="results-page-active-filters">
                <span className="results-page-filters-label">{t('results.active_filters')}:</span>
                {!isMobile && (
                  <>
                    {filters.powerGroup && (
                      <span className="results-page-filter-chip" onClick={() => setFilters(prev => ({ ...prev, powerGroup: '' }))}>
                        {t('results.power_short')}: {filters.powerGroup} ×
                      </span>
                    )}
                    {filters.ageGroup && (
                      <span className="results-page-filter-chip" onClick={() => setFilters(prev => ({ ...prev, ageGroup: '' }))}>
                        {t('results.age_short')}: {filters.ageGroup} ×
                      </span>
                    )}
                    {filters.team && (
                      <span className="results-page-filter-chip" onClick={() => setFilters(prev => ({ ...prev, team: '' }))}>
                        {t('results.team')}: {filters.team} ×
                      </span>
                    )}
                  </>
                )}
                {isMobile && (
                  <>
                    {mobileFilters.gender && (
                      <span className="results-page-filter-chip" onClick={() => setMobileFilters(prev => ({ ...prev, gender: '' }))}>
                        {mobileFilters.gender === 'male' ? t('results.male') : t('results.female')} ×
                      </span>
                    )}
                    {mobileFilters.country && (
                      <span className="results-page-filter-chip" onClick={() => setMobileFilters(prev => ({ ...prev, country: '' }))}>
                        {mobileFilters.country} ×
                      </span>
                    )}
                    {mobileFilters.powerCategory && (
                      <span className="results-page-filter-chip" onClick={() => setMobileFilters(prev => ({ ...prev, powerCategory: '' }))}>
                        {t('results.power_short')}: {mobileFilters.powerCategory} ×
                      </span>
                    )}
                    {mobileFilters.ageCategory && (
                      <span className="results-page-filter-chip" onClick={() => setMobileFilters(prev => ({ ...prev, ageCategory: '' }))}>
                        {t('results.age_short')}: {mobileFilters.ageCategory} ×
                      </span>
                    )}
                  </>
                )}
                <button 
                  className="results-page-clear-all-filters"
                  onClick={() => {
                    if (isMobile) {
                      setMobileFilters({ country: '', powerCategory: '', ageCategory: '', gender: '' });
                    } else {
                      setFilters({ ageGroup: '', powerGroup: '', team: '' });
                    }
                  }}
                >
                  {t('results.clear_all_filters')}
                </button>
              </div>
            )}

            {results.provisional && (
              <div className="results-page-provisional-notice" role="note">
                {results.provisional.type === 'tournament'
                  ? t('results.provisional_notice_tournament')
                  : t('results.provisional_notice').replace('{number}', results.provisional.number)}
              </div>
            )}

            {isMobile ? (
              <div className="results-page-results-type-selector">
                <select 
                  value={activeTab} 
                  onChange={(e) => setActiveTab(e.target.value)}
                  className="results-page-results-type-select"
                >
                  {results.event.individual_competition && (
                    <option value="individual">{t('results.individual_tab')}</option>
                  )}
                  {results.event.team_competition && (
                    <option value="team">{t('results.team_tab')}</option>
                  )}
                  <option value="routes">{t('results.routes_tab')}</option>
                  {isSelectedEventCompleted && (
                    <option value="summary">{t('results.summary_tab')}</option>
                  )}
                </select>
              </div>
            ) : (
              <div className="results-page-results-tabs">
                {results.event.individual_competition && (
                  <button 
                    className={`results-page-tab-button ${activeTab === 'individual' ? 'results-page-active' : ''}`}
                    onClick={() => setActiveTab('individual')}
                  >
                    {t('results.individual_tab')}
                  </button>
                )}
                {results.event.team_competition && (
                  <button 
                    className={`results-page-tab-button ${activeTab === 'team' ? 'results-page-active' : ''}`}
                    onClick={() => setActiveTab('team')}
                  >
                    {t('results.team_tab')}
                  </button>
                )}
                <button
                  className={`results-page-tab-button ${activeTab === 'routes' ? 'results-page-active' : ''}`}
                  onClick={() => setActiveTab('routes')}
                >
                  {t('results.routes_tab')}
                </button>
                {isSelectedEventCompleted && (
                  <button
                    className={`results-page-tab-button ${activeTab === 'summary' ? 'results-page-active' : ''}`}
                    onClick={() => setActiveTab('summary')}
                  >
                    {t('results.summary_tab')}
                  </button>
                )}
              </div>
            )}

            <div className="results-page-results-content">
              {activeTab === 'individual' && renderIndividualResults()}
              {activeTab === 'team' && renderTeamResults()}
              {activeTab === 'routes' && renderRouteResults()}
              {activeTab === 'summary' && isSelectedEventCompleted && renderSummary()}
            </div>
          </>
        ) : (
          <div className="results-page-no-results">{t('results.select_event_first')}</div>
        )}
      </div>

      <PositionProgressModal
        isOpen={progressRider !== null}
        onClose={() => setProgressRider(null)}
        riderName={progressRider ? progressRider.riderName : ''}
        points={progressRider ? progressRider.points : []}
      />

      <WeightProgressModal
        isOpen={weightRider !== null}
        onClose={() => setWeightRider(null)}
        riderName={weightRider ? weightRider.riderName : ''}
        points={weightRider ? weightRider.points : []}
      />

      {/* key по этапу и участнику: окно перезагружает телеметрию на каждое открытие */}
      <RiderCompareModal
        key={compareTarget ? `compare-${compareTarget.routeId}-${compareTarget.riderId}` : 'compare-closed'}
        isOpen={compareTarget !== null}
        onClose={() => setCompareTarget(null)}
        eventId={selectedEvent}
        routeId={compareTarget ? compareTarget.routeId : null}
        routeName={compareTarget ? compareTarget.routeName : ''}
        riderId={compareTarget ? compareTarget.riderId : null}
        currentUserId={currentUser ? currentUser.id : null}
      />

      {/* key по маршруту: окно пересоздается на каждое открытие,
          чтобы не осталось состояния от предыдущего этапа */}
      <StageReplayModal
        key={replayRoute ? `replay-${replayRoute.id}` : 'replay-closed'}
        isOpen={replayRoute !== null}
        onClose={() => setReplayRoute(null)}
        eventId={selectedEvent}
        routeId={replayRoute ? replayRoute.id : null}
        routeName={replayRoute ? replayRoute.name : ''}
      />

      {/* key по маршруту: разбор гонки перезагружается на каждое открытие */}
      <RaceAnalysisModal
        key={analysisRoute ? `analysis-${analysisRoute.id}` : 'analysis-closed'}
        isOpen={analysisRoute !== null}
        onClose={() => setAnalysisRoute(null)}
        eventId={selectedEvent}
        routeId={analysisRoute ? analysisRoute.id : null}
        routeName={analysisRoute ? analysisRoute.name : ''}
        currentUserId={currentUser ? currentUser.id : null}
      />

      <ShareResultModal
        isOpen={shareTarget !== null}
        onClose={() => setShareTarget(null)}
        eventId={selectedEvent}
        routeId={shareTarget ? shareTarget.routeId : null}
        rider={shareTarget ? { user_id: shareTarget.userId, name: shareTarget.name } : null}
      />

      {relegationRider && (
        <RelegationModal
          eventId={selectedEvent}
          rider={relegationRider}
          stages={relegationRider.stages}
          relegations={relegationRider.relegations}
          onClose={() => setRelegationRider(null)}
          onSaved={() => {
            setRelegationRider(null);
            // Места и очки этапа бэкенд уже пересчитал — забираем свежий протокол
            fetchResults(selectedEvent);
          }}
        />
      )}
    </div>
  );
};

export default EventResultsPage;